import React, {
  useState,
  useContext,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { AuthContext } from "../../context/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import logoDesktop from "../../assets/images/header-logo.png";
import logoMobile from "../../assets/images/header-logo-mobile.png";
import settings from "../../assets/images/setting.png";
import logoutIcon from "../../assets/images/logout.png";
import { useTranslation } from "react-i18next";

const DeliveryHeader = () => {
  const { logout, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(); // ← Get i18n here

  // Menu state
  const [menuOpen, setMenuOpen] = useState(false);
  const [openSection, setOpenSection] = useState(null);

  // Language state (sync with i18n)
  const [languageOpen, setLanguageOpen] = useState(false);

  // Refs
  const menuRef = useRef(null);
  const hamburgerRef = useRef(null);
  const languageRef = useRef(null);

  const userInitial = user?.da_name?.charAt(0).toUpperCase() || "U";

  /* ================= Outside Clicks ================= */
  useEffect(() => {
    if (!menuOpen) return;

    const handleClickOutside = (e) => {
      if (
        !menuRef.current?.contains(e.target) &&
        !hamburgerRef.current?.contains(e.target)
      ) {
        setMenuOpen(false);
        setOpenSection(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuOpen]);

  useEffect(() => {
    if (!languageOpen) return;

    const handleClickOutside = (e) => {
      if (!languageRef.current?.contains(e.target)) {
        setLanguageOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [languageOpen]);

  /* ================= Handlers ================= */
  const toggleSection = useCallback((section) => {
    setOpenSection((prev) => (prev === section ? null : section));
  }, []);

  const handleNavigation = () => {
    setMenuOpen(false);
    setOpenSection(null);
  };

  const handleLanguageChange = (lang) => {
    i18n.changeLanguage(lang);
    setLanguageOpen(false);
  };

  const handleLogout = useCallback(() => {
    logout();

    setMenuOpen(false);
    setOpenSection(null);
  }, [logout]);

  /* ================= Menu Config (Now Fully Translated) ================= */
  const menuSections = useMemo(
    () => ({
      account: {
        icon: (
          <div className="w-8 h-8 flex items-center justify-center rounded-full bg-blue-600 text-white font-bold">
            {userInitial}
          </div>
        ),
        title: t("yourAccount"),
        items: [
          {
            to: "/delivery-dashboard/deliverymanageaccount",
            label: t("manageYourAccount"),
          },
          {
            to: "/delivery-dashboard/pendingdelivery",
            label: t("pendingDelivery"),
          },
          {
            to: "/delivery-dashboard/deliveryhistory",
            label: t("deliveryHistory"),
          },
        ],
      },
      settings: {
        icon: settings,
        title: t("settings"),
        items: [
          { to: "/delivery-dashboard/howtouse", label: t("howToUse") },
          { to: "/delivery-dashboard/faq", label: t("faq") },
          { to: "/delivery-dashboard/support", label: t("support") },
        ],
      },
    }),
    [userInitial, t], // ← Depend on t function (changes when language changes)
  );

  /* ================= Render ================= */
  return (
    <header className="w-full bg-[#004990] sticky top-0 px-4 py-3 z-50">
      <div className="max-w-[1350px] mx-auto flex items-center justify-between">
        {/* Logo */}
        <div>
          <img
            src={logoDesktop}
            alt="Logo"
            className="hidden md:block h-[38px] cursor-pointer"
            onClick={() => navigate("/delivery-dashboard")}
          />
          <img
            src={logoMobile}
            alt="Logo"
            className="block md:hidden h-[38px] cursor-pointer"
            onClick={() => navigate("/delivery-dashboard")}
          />
        </div>

        {/* Right Controls */}
        <div className="relative flex items-center gap-4">
          {/* Language Button */}
          <button
            onClick={() => setLanguageOpen((prev) => !prev)}
            className="text-white text-xl font-semibold"
          >
            文A
          </button>

          {/* Language Popup */}
          {languageOpen && (
            <div
              ref={languageRef}
              className="absolute top-8 right-0 z-50 w-[260px] h-[200px] bg-white rounded-xl shadow-xl p-5"
            >
              <div className="flex justify-between mb-4">
                <div className="flex gap-2 font-semibold text-lg">
                  <span>文A</span>
                  <span>{t("language")}</span>
                </div>
                <button
                  onClick={() => setLanguageOpen(false)}
                  className="text-red-500 font-bold"
                >
                  ✕
                </button>
              </div>

              {[
                { code: "en", label: "English" },
                { code: "hi", label: "हिंदी" },
                { code: "mr", label: "मराठी" },
              ].map((l) => (
                <label
                  key={l.code}
                  className="flex items-center gap-3 mb-3 cursor-pointer"
                >
                  <input
                    type="radio"
                    name="language"
                    checked={i18n.language === l.code}
                    onChange={() => handleLanguageChange(l.code)}
                  />
                  <span
                    className={`font-medium ${
                      i18n.language === l.code
                        ? "text-green-600"
                        : "text-gray-700"
                    }`}
                  >
                    {l.label}
                  </span>
                </label>
              ))}
            </div>
          )}

          {/* Hamburger */}
          <button
            ref={hamburgerRef}
            onClick={() => setMenuOpen((prev) => !prev)}
          >
            <div className="space-y-1">
              <span className="block w-6 h-0.5 bg-white" />
              <span className="block w-6 h-0.5 bg-white" />
              <span className="block w-6 h-0.5 bg-white" />
            </div>
          </button>

          {/* Menu Dropdown */}
          {menuOpen && (
            <div
              ref={menuRef}
              className="absolute top-[45px] right-0 w-[280px] bg-white rounded-xl shadow-2xl overflow-hidden pt-4"
            >
              {Object.entries(menuSections).map(([key, section]) => (
                <div key={key}>
                  <div
                    className="flex justify-between items-center px-4 py-4 cursor-pointer border-b"
                    onClick={() => toggleSection(key)}
                  >
                    <div className="flex items-center gap-3">
                      {typeof section.icon === "string" ? (
                        <img src={section.icon} alt="" className="w-8 h-8" />
                      ) : (
                        section.icon
                      )}
                      <span className="text-sm font-semibold text-blue-700">
                        {section.title}
                      </span>
                    </div>
                    <span>{openSection === key ? "▲" : "▼"}</span>
                  </div>

                  {openSection === key &&
                    section.items.map((item, i) => (
                      <Link
                        key={i}
                        to={item.to}
                        onClick={handleNavigation}
                        className="block px-6 py-3 border-b hover:bg-gray-100"
                      >
                        {item.label}
                      </Link>
                    ))}
                </div>
              ))}

              {/* Logout */}
              <div
                onClick={handleLogout}
                className="flex items-center px-4 py-4 cursor-pointer hover:bg-gray-100 border-t"
              >
                <img src={logoutIcon} className="w-7 h-7 mr-3" alt="logout" />
                <span className="text-sm font-semibold text-blue-600">
                  {t("logout")}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

// Remove React.memo() or add key prop to force re-render on language change
export default DeliveryHeader; // ← Removed React.memo() to allow re-renders
